import { useEffect, useState, type ReactNode } from 'react';
import { CreditCard, ShoppingCart, Store, type LucideIcon } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { validatePhone, unformatPhone } from '@/lib/validators';
import { US_STATES } from '@/lib/constants';
import {
  useCreateCustomer,
  useUpdateCustomer,
  type CreateCustomerInput,
  type Customer,
} from '@/hooks/customer-engagement/use-customers';
import { useLocationStore } from '@/stores/location-store';

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided, the dialog runs in edit mode and pre-fills these values. */
  customer?: Customer | null;
}

// Standard form tokens — align with the Promotion form redesign. Standard
// single-line inputs use the shared <Input> component; these tokens cover the
// controls that aren't <Input> (the phone field, textarea).
const inputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;
const textareaBase = 'w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-1';
const textareaNormal = `${textareaBase} border-input focus:border-primary focus:ring-primary`;
const textareaError = `${textareaBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

// Customer-type picker cards — icon + one-line description per type, mirroring
// the Promotion form's visual type picker. Order/values are unchanged from the
// old <select>; this drives presentation only.
const CUSTOMER_TYPE_META: {
  value: 'retail' | 'credit' | 'wholesale';
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  { value: 'retail', label: 'Retail', description: 'No credit account', icon: ShoppingCart },
  { value: 'credit', label: 'Credit', description: 'House customer, pay later', icon: CreditCard },
  { value: 'wholesale', label: 'Wholesale', description: 'B2B credit', icon: Store },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALID_STATE_CODES = new Set<string>(US_STATES.map((s) => s.value));

const EMPTY: CreateCustomerInput = {
  phone: '',
  firstName: '',
  lastName: '',
  email: '',
  notes: '',
  customerType: 'retail',
  creditLimit: 0,
  businessName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
};

function customerToFormState(c: Customer): CreateCustomerInput {
  return {
    phone: unformatPhone(c.phone),
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    email: c.email ?? '',
    notes: c.notes ?? '',
    emailOptIn: c.emailOptIn,
    smsOptIn: c.smsOptIn,
    customerType: c.customerType,
    creditLimit: Number(c.creditLimit) || 0,
    businessName: c.businessName ?? '',
    addressLine1: c.addressLine1 ?? '',
    addressLine2: c.addressLine2 ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    postalCode: c.postalCode ?? '',
  };
}

export function CreateCustomerDialog({ open, onClose, customer }: Props) {
  const isEdit = !!customer;
  const [form, setForm] = useState<CreateCustomerInput>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const submitting = create.isPending || update.isPending;
  // Pulled from the header location picker. Becomes the new customer's
  // homeLocationId so the per-location super-admin Customer Engagement
  // gate has something to evaluate against.
  const { selectedLocationId } = useLocationStore();

  useEffect(() => {
    if (!open) return;
    setForm(customer ? customerToFormState(customer) : EMPTY);
    setFieldErrors({});
  }, [open, customer]);

  const handleClose = () => {
    setForm(EMPTY);
    setFieldErrors({});
    onClose();
  };

  const set = <K extends keyof CreateCustomerInput>(key: K, value: CreateCustomerInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors((e) => {
        const { [key as string]: _, ...rest } = e;
        return rest;
      });
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    const phoneErr = validatePhone(form.phone, true);
    if (phoneErr) errors.phone = phoneErr;

    const email = form.email?.trim();
    if (!email) {
      errors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(email)) {
      errors.email = 'Please enter a valid email';
    }

    if ((form.firstName?.length ?? 0) > 100) errors.firstName = 'First name must be 100 characters or fewer';
    if ((form.lastName?.length ?? 0) > 100) errors.lastName = 'Last name must be 100 characters or fewer';
    if ((form.notes?.length ?? 0) > 1000) errors.notes = 'Notes must be 1000 characters or fewer';

    if (form.customerType !== 'retail') {
      const limit = form.creditLimit ?? 0;
      if (Number.isNaN(limit) || limit < 0) {
        errors.creditLimit = 'Credit limit cannot be negative';
      }
    }

    const state = form.state?.trim();
    if (state && !VALID_STATE_CODES.has(state)) {
      errors.state = 'Please select a valid state';
    }

    const postal = form.postalCode?.trim();
    if (postal && !/^\d{5}$/.test(postal)) {
      errors.postalCode = 'Postal code must be 5 digits';
    }

    const city = form.city?.trim();
    if (city && !/^[A-Za-z\s]+$/.test(city)) {
      errors.city = 'City can only contain letters and spaces';
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (isEdit && customer) {
        const patch = {
          firstName: form.firstName?.trim() || null,
          lastName: form.lastName?.trim() || null,
          email: form.email?.trim() || null,
          notes: form.notes?.trim() || null,
          emailOptIn: form.emailOptIn ?? false,
          smsOptIn: form.smsOptIn ?? false,
          customerType: form.customerType,
          creditLimit: form.customerType === 'retail' ? 0 : Number(form.creditLimit ?? 0),
          businessName: form.businessName?.trim() || null,
          addressLine1: form.addressLine1?.trim() || null,
          addressLine2: form.addressLine2?.trim() || null,
          city: form.city?.trim() || null,
          state: form.state?.trim().toUpperCase() || null,
          postalCode: form.postalCode?.trim() || null,
        };
        const result = await update.mutateAsync({ id: customer.id, patch });
        toast.success(`Customer "${result.data.name || result.data.phone}" updated`);
      } else {
        const payload: CreateCustomerInput = {
          phone: form.phone,
          firstName: form.firstName?.trim() || undefined,
          lastName: form.lastName?.trim() || undefined,
          email: form.email?.trim() || undefined,
          notes: form.notes?.trim() || undefined,
          // Opt-in flags are in the form but were previously dropped from
          // the create payload, which silently forced every new customer
          // to opted-out regardless of what the cashier ticked. Same
          // fields the edit path already sends.
          emailOptIn: form.emailOptIn ?? false,
          smsOptIn: form.smsOptIn ?? false,
          customerType: form.customerType,
          creditLimit: form.customerType === 'retail' ? undefined : form.creditLimit,
          businessName: form.businessName?.trim() || undefined,
          addressLine1: form.addressLine1?.trim() || undefined,
          addressLine2: form.addressLine2?.trim() || undefined,
          city: form.city?.trim() || undefined,
          state: form.state?.trim().toUpperCase() || undefined,
          postalCode: form.postalCode?.trim() || undefined,
          // Header location picker fills homeLocationId so the per-location
          // super-admin Customer Engagement gate has something to check
          // against. Without this the welcome SMS would skip with
          // reason=no-location.
          homeLocationId: selectedLocationId || undefined,
        };
        const result = await create.mutateAsync(payload);
        toast.success(`Customer "${result.data.name || result.data.phone}" created`);
      }
      handleClose();
    } catch (e: any) {
      toast.error(e?.error?.message ?? `Failed to ${isEdit ? 'update' : 'create'} customer`);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit Customer' : 'Create Customer'}
      widthClass="max-w-[760px]"
      footer={
        <div className="flex items-center gap-3">
          <span className="mr-auto hidden text-xs text-muted-foreground sm:block">
            {isEdit ? 'Changes are saved immediately.' : 'Email and phone number are required.'}
          </span>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form="customer-form" disabled={submitting}>
            {submitting ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save Customer' : 'Create Customer'}
          </Button>
        </div>
      }
    >
      <form id="customer-form" onSubmit={handleSubmit} noValidate className="space-y-7">
        {/* ── 1 · basic details (name + contact + notes + opt-ins) ─── */}
        <FormSection num={1} title="Basic Details">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cust-first-name">First Name</Label>
              <Input
                id="cust-first-name"
                className="mt-1"
                type="text"
                value={form.firstName ?? ''}
                onChange={(e) => set('firstName', e.target.value)}
                placeholder="John"
                maxLength={100}
                error={!!fieldErrors.firstName}
                aria-invalid={!!fieldErrors.firstName}
              />
              {fieldErrors.firstName && <p className="mt-1 text-xs text-red-500">{fieldErrors.firstName}</p>}
            </div>
            <div>
              <Label htmlFor="cust-last-name">Last Name</Label>
              <Input
                id="cust-last-name"
                className="mt-1"
                type="text"
                value={form.lastName ?? ''}
                onChange={(e) => set('lastName', e.target.value)}
                placeholder="Doe"
                maxLength={100}
                error={!!fieldErrors.lastName}
                aria-invalid={!!fieldErrors.lastName}
              />
              {fieldErrors.lastName && <p className="mt-1 text-xs text-red-500">{fieldErrors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cust-email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="cust-email"
                className="mt-1"
                type="email"
                value={form.email ?? ''}
                onChange={(e) => set('email', e.target.value)}
                placeholder="john@example.com"
                maxLength={255}
                error={!!fieldErrors.email}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
            </div>
            <div>
              <Label htmlFor="cust-phone">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <PhoneInput
                id="cust-phone"
                value={form.phone}
                onValueChange={(digits) => set('phone', digits)}
                placeholder="123-456-7890"
                className={`mt-1 ${fieldErrors.phone ? inputError : inputNormal}`}
                disabled={isEdit}
                aria-invalid={!!fieldErrors.phone}
              />
              {fieldErrors.phone ? (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>
              ) : isEdit ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Phone is the customer identifier and cannot be changed.
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <Label htmlFor="cust-notes">Notes</Label>
            <textarea
              id="cust-notes"
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              maxLength={1000}
              className={`mt-1 ${fieldErrors.notes ? textareaError : textareaNormal}`}
              aria-invalid={!!fieldErrors.notes}
            />
            {fieldErrors.notes && <p className="mt-1 text-xs text-red-500">{fieldErrors.notes}</p>}
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.emailOptIn ?? false}
                onChange={(e) => set('emailOptIn', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              Email subscription opt-in
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.smsOptIn ?? false}
                onChange={(e) => set('smsOptIn', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              SMS subscription opt-in
            </label>
          </div>
        </FormSection>

        {/* ── 2 · credit account ───────────────────────────────────── */}
        <FormSection num={2} title="Credit account">
          <div className="rounded-xl border border-border bg-muted/30 p-4">
            <p className="mb-3 text-xs text-muted-foreground">
              Only customers of type <strong>credit</strong> or <strong>wholesale</strong> can use Pay-by-Credit at the
              register.
            </p>

            <div className="space-y-3">
              <div>
                <Label>Customer Type</Label>
                <div className="mt-1 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                  {CUSTOMER_TYPE_META.map((t) => {
                    const Icon = t.icon;
                    const selected = form.customerType === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => set('customerType', t.value)}
                        aria-pressed={selected}
                        className={cn(
                          'flex items-start gap-3 rounded-xl border p-3 text-left transition',
                          selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40',
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                            selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-foreground">{t.label}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">{t.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="sm:max-w-xs">
                <Label htmlFor="cust-credit-limit">
                  Credit Limit ($){form.customerType !== 'retail' && <span className="text-red-500"> *</span>}
                </Label>
                <Input
                  id="cust-credit-limit"
                  className="mt-1 disabled:cursor-not-allowed disabled:opacity-60"
                  type="number"
                  selectOnFocus
                  value={form.creditLimit ?? 0}
                  onChange={(e) => set('creditLimit', Number(e.target.value))}
                  step="0.01"
                  min="0"
                  disabled={form.customerType === 'retail'}
                  error={!!fieldErrors.creditLimit}
                  aria-invalid={!!fieldErrors.creditLimit}
                />
                {fieldErrors.creditLimit ? (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.creditLimit}</p>
                ) : form.customerType !== 'retail' ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    The register blocks sales that exceed this limit (manager-PIN override available).
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">Not used for retail customers.</p>
                )}
              </div>
            </div>
          </div>
        </FormSection>

        {/* ── 3 · business & address ───────────────────────────────── */}
        <FormSection num={3} title="Business & address">
          <div>
            <Label htmlFor="cust-business">Business Name</Label>
            <Input
              id="cust-business"
              className="mt-1"
              type="text"
              value={form.businessName ?? ''}
              onChange={(e) => set('businessName', e.target.value)}
              placeholder="Acme Co."
              maxLength={255}
            />
          </div>

          <div>
            <Label htmlFor="cust-address1">Address Line 1</Label>
            <Input
              id="cust-address1"
              className="mt-1"
              type="text"
              value={form.addressLine1 ?? ''}
              onChange={(e) => set('addressLine1', e.target.value)}
              maxLength={255}
            />
          </div>

          <div>
            <Label htmlFor="cust-address2">Address Line 2</Label>
            <Input
              id="cust-address2"
              className="mt-1"
              type="text"
              value={form.addressLine2 ?? ''}
              onChange={(e) => set('addressLine2', e.target.value)}
              maxLength={255}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="cust-city">City</Label>
              <Input
                id="cust-city"
                className="mt-1"
                type="text"
                value={form.city ?? ''}
                onChange={(e) => set('city', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                placeholder="San Francisco"
                maxLength={100}
                error={!!fieldErrors.city}
                aria-invalid={!!fieldErrors.city}
              />
              {fieldErrors.city && <p className="mt-1 text-xs text-red-500">{fieldErrors.city}</p>}
            </div>
            <div>
              <Label>State</Label>
              <div className="mt-1">
                <SearchableSelect
                  options={US_STATES.map((s) => ({ value: s.value, label: s.label }))}
                  value={form.state ?? ''}
                  onChange={(next) => set('state', next)}
                  placeholder="Select"
                  searchPlaceholder="Search states..."
                  emptyMessage="No states found"
                  error={!!fieldErrors.state}
                />
              </div>
              {fieldErrors.state && <p className="mt-1 text-xs text-red-500">{fieldErrors.state}</p>}
            </div>
            <div>
              <Label htmlFor="cust-postal">Postal Code</Label>
              <Input
                id="cust-postal"
                className="mt-1"
                type="text"
                value={form.postalCode ?? ''}
                onChange={(e) => set('postalCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="94105"
                maxLength={5}
                inputMode="numeric"
                error={!!fieldErrors.postalCode}
                aria-invalid={!!fieldErrors.postalCode}
              />
              {fieldErrors.postalCode && <p className="mt-1 text-xs text-red-500">{fieldErrors.postalCode}</p>}
            </div>
          </div>
        </FormSection>
      </form>
    </Modal>
  );
}

// A numbered, titled group of fields — the same visual "spine" the Promotion
// form uses (1 · who they are, 2 · contact, …) so the two dialogs read as one
// design system. Purely presentational — no state, no logic.
function FormSection({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold leading-none text-primary">
          {num}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
